import { useState } from 'react'
import { CodemaxBrand } from '@/components/Brand'
import { Button } from '@/components/ui/primitives'
import { useAuth } from '@/lib/auth'

/**
 * There is no form here, and there never will be.
 *
 * Codemax ARMS holds the password and sends the activation link; this portal never sees
 * either, which is the point of single sign-on and the reason there is nothing on this
 * page to phish.
 *
 * There is also no sign-up. A portal account exists because somebody at Codemax invited
 * it, and ARMS provisioned it before the row was saved.
 */
export function SignIn() {
  const { signIn, switchAccount, refusal, error } = useAuth()
  const [busy, setBusy] = useState(false)

  async function run(action: () => Promise<void>) {
    setBusy(true)
    try {
      await action()
    } finally {
      // Only reached if the redirect never happened; on the happy path the page is gone.
      setBusy(false)
    }
  }

  return (
    <div className="grid min-h-full lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          {/* The header's lockup, at the size a page title wants: mark, hairline, name.
              Same three parts and the same order, so the first thing somebody sees on the
              way in is the thing that stays in front of them afterwards. Stacked, the mark
              read as a separate logo above an unrelated heading. */}
          <div className="flex items-center gap-3">
            <CodemaxBrand className="h-6" />
            <span aria-hidden className="h-6 w-px bg-line" />
            <h1 className="whitespace-nowrap text-xl font-semibold tracking-[-0.02em]">
              Reseller Portal
            </h1>
          </div>

          <p className="mt-3 text-[13px] text-muted">
            Your deals, your commission and your remittances &mdash; in one place, from the
            source.
          </p>

          {/* Codemax authenticated them and the portal will not have them. Saying so
              plainly matters: without it the only signal is a sign-in button that appears
              to do nothing, and they try it again all afternoon. */}
          {refusal && (
            <div
              role="alert"
              className="mt-7 rounded-md border border-warn/30 bg-warn-wash px-4 py-3"
            >
              <p className="text-[13px] font-medium text-warn">{refusal.title}</p>
              <p className="mt-1 text-[12px] text-ink-2">{refusal.detail ?? refusal.message}</p>
            </div>
          )}

          {error && (
            <div role="alert" className="mt-7 rounded-md border border-crit/30 bg-crit-wash px-4 py-3">
              <p className="text-[13px] font-medium text-crit">Cannot reach Codemax sign-in</p>
              <p className="mt-1 text-[12px] text-ink-2">{error}</p>
            </div>
          )}

          {/* Which button leads depends on what happened. A refusal means the Codemax
              account signing in is the wrong one, and pressing Continue would fetch the
              same token from the same ARMS cookie and be refused again. Changing account
              is the action that can actually succeed, so it leads. */}
          {refusal ? (
            <>
              <Button
                variant="primary"
                loading={busy}
                className="mt-7 w-full"
                onClick={() => void run(switchAccount)}
              >
                Sign in as a different Codemax account
              </Button>

              <Button
                variant="secondary"
                className="mt-3 w-full"
                onClick={() => window.location.assign('/')}
              >
                Try again
              </Button>

              <p className="mt-3 text-[12px] text-muted">
                Changing account signs you out of Codemax on this browser first. Try again
                is for after your Codemax contact has invited this address.
              </p>
            </>
          ) : (
            <>
              <Button
                variant="primary"
                loading={busy}
                className="mt-7 w-full"
                onClick={() => void run(signIn)}
              >
                Sign in with Codemax
              </Button>

              <p className="mt-3 text-[12px] text-muted">
                Codemax holds your password and sends your activation link. This portal
                never sees it, and there is no sign-up &mdash; your account exists because
                your Codemax contact created it.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Brand punctuation, and nothing else. No stock photography and no marketing copy:
          the person signing in is a partner who already has an agreement with Codemax. */}
      <div className="brand-gradient hidden items-end p-10 lg:flex">
        <div className="max-w-md text-white">
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] opacity-80">Codemax</p>
          <p className="mt-3 text-2xl font-semibold leading-snug">
            What you sourced, what it earned, and what we have paid you.
          </p>
        </div>
      </div>
    </div>
  )
}
